import { useOutbox, type QueuedSale } from '../../store/outbox';
import { useReceiptStore } from '../../store/receipt';
import { useCurrency } from '../print/useCurrency';
import { useLang } from '../../lib/language';
import { useNetwork } from '../../lib/netStatus';

/**
 * The sales this till took while it had no network, and what has become of
 * them.
 *
 * This screen answers one question a cashier will absolutely ask — "is my
 * money in yet?" — and it has to answer it without them understanding
 * anything about queues. So: a count, a button, and one row per sale saying
 * either "waiting", "saved as invoice N", or what went wrong.
 *
 * A failed row is money the books do not have. It is never auto-dismissed and
 * it blocks shift close, because the alternative is a cashier going home with
 * a variance nobody can explain.
 */
export function OfflineQueueScreen() {
  const { t } = useLang();
  const entries = useOutbox((s) => s.entries);
  const syncing = useOutbox((s) => s.syncing);
  const sync = useOutbox((s) => s.sync);
  const online = useNetwork((s) => s.online);

  const pending = entries.filter((e) => e.status === 'queued' || e.status === 'sending');
  const failed = entries.filter((e) => e.status === 'failed');
  const synced = entries.filter((e) => e.status === 'synced');

  // Newest first — the sale a cashier is asking about is nearly always the
  // last one they took.
  const ordered = [...entries].sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));

  return (
    <div style={{ padding: 24, overflowY: 'auto', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            {t({ en: 'Offline sales', ar: 'المبيعات دون اتصال' })}
          </h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {pending.length > 0
              ? t({
                  en: `${pending.length} waiting to be saved`,
                  ar: `${pending.length} في انتظار الحفظ`,
                })
              : failed.length > 0
                ? t({ en: 'Nothing waiting — but see below', ar: 'لا شيء في الانتظار — لكن انظر أدناه' })
                : t({ en: 'Everything is saved', ar: 'تم حفظ كل شيء' })}
            {!online && ` · ${t({ en: 'no connection', ar: 'لا يوجد اتصال' })}`}
          </div>
        </div>

        <button
          className="btn-primary"
          style={{ minWidth: 160 }}
          disabled={syncing || pending.length === 0}
          onClick={() => void sync()}
        >
          {syncing
            ? t({ en: 'Syncing…', ar: 'جارٍ المزامنة…' })
            : t({ en: 'Sync now', ar: 'زامن الآن' })}
        </button>
      </div>

      {entries.length === 0 && (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 14,
          }}
        >
          {t({
            en: 'No offline sales. Everything this till has taken went straight to the server.',
            ar: 'لا توجد مبيعات دون اتصال. كل ما سجلته هذه النقطة ذهب مباشرة إلى الخادم.',
          })}
        </div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {ordered.map((e) => (
          <QueueRow key={e.id} entry={e} />
        ))}
      </div>

      {synced.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 20 }}>
          {t({
            en: 'Saved sales stay listed for a week so you can match them against the invoice numbers.',
            ar: 'تبقى المبيعات المحفوظة معروضة لمدة أسبوع حتى يمكنك مطابقتها بأرقام الفواتير.',
          })}
        </p>
      )}
    </div>
  );
}

function QueueRow({ entry }: { entry: QueuedSale }) {
  const { t } = useLang();
  const requeue = useOutbox((s) => s.requeue);
  const currency = useCurrency();
  const digits = Math.max(0, Math.min(8, currency.precision));
  const money = (n: number): string => `${(Number.isFinite(n) ? n : 0).toFixed(digits)} ${currency.code}`;

  const tone =
    entry.status === 'synced' ? 'var(--success, #1a6f52)'
      : entry.status === 'failed' ? 'var(--danger, #9d2b22)'
        : 'var(--text-muted)';

  const when = new Date(entry.capturedAt);
  const clock = Number.isNaN(when.getTime())
    ? entry.capturedAt
    : when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${tone}`,
        borderRadius: 8,
        padding: '12px 14px',
        background: 'var(--surface)',
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>
          {entry.provisionalNo}
          {entry.result && (
            <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>
              {' → '}
              {t({ en: 'invoice', ar: 'فاتورة' })} #{entry.result.entryNo}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
          {clock} · {money(entry.summary.total)} ·{' '}
          {entry.summary.itemCount} {t({ en: 'items', ar: 'صنف' })}
          {entry.summary.customerLabel ? ` · ${entry.summary.customerLabel}` : ''}
        </div>
        {entry.status === 'failed' && entry.lastError && (
          <div style={{ fontSize: 12.5, color: 'var(--danger, #9d2b22)', marginTop: 5 }}>
            {entry.lastError.message}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, color: tone, fontWeight: 600 }}>
          {entry.status === 'synced'
            ? t({ en: 'Saved', ar: 'محفوظ' })
            : entry.status === 'failed'
              ? t({ en: 'Not saved', ar: 'لم يُحفظ' })
              : entry.status === 'sending'
                ? t({ en: 'Sending…', ar: 'جارٍ الإرسال…' })
                : t({ en: 'Waiting', ar: 'في الانتظار' })}
        </span>

        {entry.receipt && (
          <button
            className="btn-secondary"
            onClick={() => void useReceiptStore.getState().pushAndPrint(entry.receipt!)}
          >
            {t({ en: 'Print', ar: 'طباعة' })}
          </button>
        )}

        {entry.status === 'failed' && (
          <button className="btn-secondary" onClick={() => void requeue(entry.id)}>
            {t({ en: 'Try again', ar: 'أعد المحاولة' })}
          </button>
        )}
      </div>
    </div>
  );
}
